/**
 * QueueDigestIndexPage — Shared index page for queue/digest lists
 *
 * Parameterized by app context. Shows the user's queue/digest profiles
 * as clickable cards. Uses existing screen-stats endpoint (no new backend).
 *
 * Terminology: LitScreen uses "Queue", all others use "Digest".
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { navV2Routes, type AppKey } from '../../hooks/useNavV2';
import { preferencesAPI } from '../../api';
import { ArrowLeft, Loader2, FileSearch, BookOpen, Headphones, MessageSquare, GraduationCap, Globe } from 'lucide-react';

interface Props {
  app: AppKey;
}

const APP_META: Record<AppKey, { title: string; term: string; icon: React.ElementType; color: string }> = {
  'screen':        { title: 'LitScreen',  term: 'Queue',  icon: FileSearch,    color: 'text-sky-600' },
  'learn':         { title: 'LitHub',     term: 'Digest', icon: BookOpen,      color: 'text-emerald-600' },
  'audio-digests': { title: 'LitStream',  term: 'Digest', icon: Headphones,    color: 'text-violet-600' },
  'litscholar':    { title: 'LitScholar', term: 'Digest', icon: GraduationCap, color: 'text-rose-600' },
  'community':     { title: 'LitClub',   term: 'Digest', icon: MessageSquare, color: 'text-amber-600' },
  'litportal':     { title: 'LitPortal',  term: 'Digest', icon: Globe,         color: 'text-teal-600' },
};

const QueueDigestIndexPage: React.FC<Props> = ({ app }) => {
  const navigate = useNavigate();
  const meta = APP_META[app];
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const stats = await preferencesAPI.getProfilesScreenStats();
        setProfiles(stats.profiles || []);
      } catch {}
      setLoading(false);
    })();
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid={`navv2-index-${app}`}>
      <button onClick={() => navigate(navV2Routes.root(app))} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to {meta.title}
      </button>

      <div className="flex items-center gap-3 mb-6">
        <meta.icon className={`w-6 h-6 ${meta.color}`} />
        <h1 className="text-xl font-bold text-slate-900">Your {meta.term}s</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <p className="text-sm">No {meta.term.toLowerCase()}s configured yet.</p>
          <button onClick={() => navigate('/preferences')} className="mt-3 text-sm text-sky-600 hover:text-sky-700 font-medium">
            Set up your first {meta.term.toLowerCase()} →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map((p: any) => (
            <button
              key={p.profile_id}
              onClick={() => navigate(navV2Routes.profileDetail(app, p.profile_id))}
              className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-sm transition-all text-left"
              data-testid={`navv2-profile-${p.profile_id}`}
            >
              <div>
                <p className="font-semibold text-slate-800">{p.specialty_label || 'Unknown'}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {p.subspecialty_label && <span>{p.subspecialty_label} · </span>}
                  {p.is_primary && <span className="text-amber-600 font-medium">Primary · </span>}
                  {p.unscreened_count > 0 && <span className="text-sky-600">{p.unscreened_count} unscreened</span>}
                  {p.last_digest_date && <span className="text-slate-400"> · Last: {p.last_digest_date}</span>}
                </p>
              </div>
              <span className="text-slate-300">→</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QueueDigestIndexPage;
